// ─── BriefPreview ────────────────────────────────────────────────────────
// Pure rendering of a BriefEnvelope as a human-readable section.
// Used by RunDetailPage (input params card). It was extracted from ExamplesPanel,
// which is gone with the Examples Library (2026-09-04) — this component stayed
// because the run inspector is now its only, and sufficient, consumer.
// No fetching, no state — drives entirely from props.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_MONO } from '../../constants/theme.js';
import { translateOptionLabel, resolveToneLabel } from '../../utils/optionLabels.js';

const previewSnippet = (text, max = 280) => {
  if (!text) return '';
  const t = text.trim().replace(/\s+/g, ' ');
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
};

export default function BriefPreview({ brief, t, color = COLORS.accent, tonePresets = [] }) {
  const { t: tr } = useTranslation('wizard');
  if (!brief) {
    return (
      <div style={{
        color: t.textFaint, fontStyle: 'italic', lineHeight: 1.5,
        padding: '20px 0', textAlign: 'center',
      }}>
        {tr('briefPreview.noBrief')}
      </div>
    );
  }

  const b = brief;
  const cp = b.content_params || {};
  const rs = b.research_sources || {};
  const kw = cp.keywords || {};
  const secondaryKw = Array.isArray(kw.secondary)
    ? kw.secondary.map(k => (typeof k === 'string' ? k : k?.keyword)).filter(Boolean)
    : [];
  const sourceLabels = [
    rs.use_company_website && tr('briefPreview.sources.companyWebsite'),
    rs.use_company_docs && tr('briefPreview.sources.knowledgeBase'),
    rs.use_web_search && tr('briefPreview.sources.webSearch'),
    rs.use_uploaded_documents && tr('briefPreview.sources.uploadedDocs'),
  ].filter(Boolean);
  const requestUrls = Array.isArray(rs.request_urls) ? rs.request_urls.filter(Boolean) : [];

  const sectionLabel = { fontWeight: 600, marginBottom: '4px', color: t.text };
  const sectionBody  = { marginBottom: '10px', color: t.textMuted, lineHeight: 1.5 };
  const chip = {
    display: 'inline-block', fontSize: '10px', fontFamily: FONT_MONO,
    padding: '2px 8px', borderRadius: '100px',
    background: `${color}15`, color, letterSpacing: '0.4px',
    textTransform: 'uppercase', marginRight: '4px', marginBottom: '4px',
  };

  return (
    <>
      <div style={sectionLabel}>{tr('briefPreview.topic')}</div>
      <div style={sectionBody}>{b.topic || '—'}</div>

      {(b.platform || b.objective || b.persona) && (
        <div style={{ marginBottom: '10px' }}>
          {b.platform  && <span style={chip}>{b.platform}</span>}
          {b.objective && <span style={chip}>{translateOptionLabel(tr, 'objective', b.objective, [], b.objective)}</span>}
          {b.persona   && <span style={chip}>{translateOptionLabel(tr, 'persona',   b.persona,   [], b.persona)}</span>}
        </div>
      )}

      {(() => {
        const toneVal = cp.tone_hint || b.tone_hint;
        // [FIX-v161] Stesso percorso i18n del form (options.<group>.<value>): senza
        // questo la stessa run mostrava lo slug di wire ("drive-traffic") qui e la
        // label tradotta ovunque altro. Il fallback è SEMPRE il valore grezzo, così
        // un tone_hint in testo libero o uno slug senza traduzione resta visibile
        // tale e quale.
        const optLabel = (group, val) => translateOptionLabel(tr, group, val, [], val);
        const rows = [
          toneVal             && [tr('briefPreview.params.tone'),          resolveToneLabel(tr, toneVal, tonePresets)],
          cp.article_length   && [tr('briefPreview.params.length'),     optLabel('blogLength', cp.article_length)],
          cp.seo_intent       && [tr('briefPreview.params.seoIntent'),    optLabel('seoIntent', cp.seo_intent)],
          cp.post_length      && [tr('briefPreview.params.postLength'), optLabel('postLength', cp.post_length)],
          cp.cta_type         && [tr('briefPreview.params.ctaType'),      optLabel('ctaType', cp.cta_type)],
          cp.slide_count      && [tr('briefPreview.params.slide'),         optLabel('carouselSlide', cp.slide_count)],
          cp.email_length     && [tr('briefPreview.params.emailLength'), optLabel('emailLength', cp.email_length)],
          cp.include_companion_post && [tr('briefPreview.params.companionPost'), tr('briefPreview.params.yes')],
          cp.ads_channel      && [tr('briefPreview.params.adsChannel'),    optLabel('adsChannel', cp.ads_channel)],
          cp.ads_objective    && [tr('briefPreview.params.adsObjective'), cp.ads_objective],
          cp.ad_groups        && [tr('briefPreview.params.adGroups'),     optLabel('adGroups', cp.ad_groups)],
          cp.ad_count         && [tr('briefPreview.params.adCount'),    cp.ad_count],
          cp.output_style     && [tr('briefPreview.params.outputStyle'),  optLabel('caseStudySection', cp.output_style)],
          cp.focus_area       && [tr('briefPreview.params.focusArea'),    optLabel('caseStudyFocus', cp.focus_area)],
          cp.headline_formula && [tr('briefPreview.params.headlineFormula'), optLabel('landingHeadline', cp.headline_formula)],
        ].filter(Boolean);
        if (rows.length === 0) return null;
        return (
          <>
            <div style={sectionLabel}>{tr('briefPreview.sections.params')}</div>
            <div style={sectionBody}>
              {rows.map(([k, v]) => <div key={k}>{k}: {v}</div>)}
            </div>
          </>
        );
      })()}

      {(kw.primary || secondaryKw.length > 0) && (
        <>
          <div style={sectionLabel}>{tr('briefPreview.sections.keyword')}</div>
          <div style={sectionBody}>
            {kw.primary && <div><strong style={{ color: t.text }}>{tr('briefPreview.kw.primary')}</strong> {kw.primary}</div>}
            {secondaryKw.length > 0 && <div><strong style={{ color: t.text }}>{tr('briefPreview.kw.secondary')}</strong> {secondaryKw.join(', ')}</div>}
          </div>
        </>
      )}

      {Array.isArray(b.key_points) && b.key_points.length > 0 && (
        <>
          <div style={sectionLabel}>{tr('briefPreview.sections.keyPoints')}</div>
          <ul style={{ margin: '0 0 10px 0', paddingLeft: '18px', color: t.textMuted, lineHeight: 1.5 }}>
            {b.key_points.map((kp, i) => <li key={i}>{kp}</li>)}
          </ul>
        </>
      )}

      {b.additional_context && (
        <>
          <div style={sectionLabel}>{tr('briefPreview.sections.additionalContext')}</div>
          <div style={sectionBody}>{previewSnippet(b.additional_context, 600)}</div>
        </>
      )}

      {(sourceLabels.length > 0 || rs.web_search_query || rs.kb_search_query || requestUrls.length > 0 || b.fact_check?.enabled) && (
        <>
          <div style={sectionLabel}>{tr('briefPreview.sections.sourcesResearch')}</div>
          <div style={sectionBody}>
            {sourceLabels.length > 0 && (
              <div style={{ marginBottom: '4px' }}>
                {sourceLabels.map(s => <span key={s} style={chip}>{s}</span>)}
              </div>
            )}
            {rs.web_search_query && <div>{tr('briefPreview.webQuery')} <em>{rs.web_search_query}</em></div>}
            {rs.kb_search_query  && <div>{tr('briefPreview.kbQuery')} <em>{rs.kb_search_query}</em></div>}
            {requestUrls.length > 0 && (
              <div style={{ marginTop: '4px' }}>
                <div>{tr('briefPreview.requestedUrls')}</div>
                <ul style={{ margin: '2px 0 0 0', paddingLeft: '18px' }}>
                  {requestUrls.map((u, i) => (
                    <li key={i} style={{ wordBreak: 'break-all' }}>
                      <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: t.textMuted, textDecoration: 'underline' }}>{u}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {b.fact_check?.enabled && <div style={{ marginTop: '4px' }}><span style={chip}>{tr('briefPreview.factCheckActive')}</span></div>}
          </div>
        </>
      )}

      {b.cta && (
        <>
          <div style={sectionLabel}>{tr('briefPreview.sections.cta')}</div>
          <div style={{ color: t.textMuted, lineHeight: 1.5 }}>
            {b.cta}
            {b.cta_url && (
              <span style={{ color: t.textFaint, fontSize: '11px', display: 'block', marginTop: '2px' }}>
                → {b.cta_url}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
}
